/**
 * The supplier portal's warehouse picks (docs/features/supplier-interface/14_warehouse_picks_update_spec.md, §5 and
 * §8): the second document type beside purchase orders, seen only by a signed-in supplier person whose supplier
 * runs a warehouse for us. A separate interface from `SupplierPortalDataAdapter` because it is served on its own:
 * the real implementation (`./api/supplier-portal.ts`, `/supplier-api/picks`) exists, the purchase-order half does
 * not yet.
 *
 * Picks are logins only (no link access), so no call carries an access value: the session cookie is the credential,
 * and a refused credential reads as `signed_out`. The server decides scope (the supplier's warehouses) and what a
 * pick allows (`actions`); pages render. A pick out of scope or unknown is `not_found`, never "forbidden".
 *
 * **Despatch is per delivery line.** A pick can go to several addresses, each with its own carrier and tracking, so
 * a despatch names the pick and (when the pick has several destinations) the delivery line and quantity. One call
 * records one pick or many; each entry stands alone, so a bulk call can partly succeed (`PickDespatchResult`).
 * A request the server refuses whole is an `ApiError`: 400 with field errors in the `.claude/rules/api-validation.md`
 * shape (`despatches.0.despatch_ref`), read with `extractFieldErrors`.
 *
 * The pick id is internal and never shown: the line reference (`ORD-K48193.01`) is what the warehouse quotes to us.
 */

import type { ListResponse } from "./adapter";
import type { Carrier } from "./supplier-portal-adapter";
import type {
  BookPickCourierRequest,
  BookPickCourierResponse,
  PickDespatchResult,
  SupplierDryRun,
  SupplierPickPage,
  SupplierPickRow,
  SupplierPickTabCounts,
} from "./supplier-api-types";

export type {
  BookPickCourierRequest,
  BookPickCourierResponse,
  PickDeliverTo,
  PickDespatch,
  PickDespatchOutcome,
  PickDespatchResult,
  PickRefusalReason,
  SuggestedPickBatch,
  SupplierPickPage,
  SupplierPickRow,
  SupplierPickState,
  SupplierDryRun,
  SupplierPickTabCounts,
} from "./supplier-api-types";

export type SupplierPickTab = keyof SupplierPickTabCounts;

export const SUPPLIER_PICK_TABS: readonly SupplierPickTab[] = [
  "to_pick",
  "despatched_today",
  "all",
];

/** The server's allowed sorts: oldest first by default. */
export type SupplierPickSort =
  "created_at" | "-created_at" | "required_on" | "-required_on";

export interface SupplierPickListOptions {
  /** Null when the supplier runs one warehouse (the server defaults it); required when it runs several. */
  warehouseId: string | null;
  tab: SupplierPickTab;
  sort?: SupplierPickSort;
  q?: string;
  page?: number;
  pageSize?: number;
}

export interface SupplierPickList extends ListResponse<SupplierPickRow> {
  /** The warehouse listed. */
  warehouseId: string;
  /** Every tab's count under the same search. */
  tabCounts: SupplierPickTabCounts;
}

/** A pick read: a login that is not signed in is `signed_out`; an unknown or out-of-scope pick is `not_found`. */
export type SupplierPickRead =
  | { kind: "ok"; page: SupplierPickPage }
  | { kind: "not_found" }
  | { kind: "signed_out" };

/** One despatch to record: a pick (and, for a pick that goes to several addresses, one destination of it). */
export interface PickDespatchInput {
  pick_id: string;
  /** The warehouse's despatch note or parcel reference: 1–64 letters, digits, `_` or `-`. One parcel covering
   *  several picks passes the same ref on each. */
  despatch_ref: string;
  despatched_on: string;
  carrier: Carrier;
  /** Required for `other`. */
  carrier_name?: string | null;
  /** None for `own_van`. */
  tracking_number?: string | null;
  /** The destination it went to; may be left out when the pick has one. */
  delivery_line_id?: string | null;
  /** How many went; all still to go to that destination when left out. */
  quantity?: number | null;
  /** The batch picked; the suggestion when left out. */
  batch_id?: string | null;
}

export interface SupplierPicksAdapter {
  /** Refusals are an `ApiError`: 400 `warehouse_id` required (several warehouses, none named), 404 a warehouse not
   *  in scope, 401 not signed in, 422 an unknown sort. */
  listPicks(options: SupplierPickListOptions): Promise<SupplierPickList>;
  getPick(pickId: string): Promise<SupplierPickRead>;
  /** One result per entry, in the request's order. */
  recordPickDespatches(
    despatches: PickDespatchInput[],
  ): Promise<PickDespatchResult[]>;
  /**
   * *Book with DPD / DX*'s dry run for one pick (courier chain): the carriers booked here (choice `carrier`; none
   * means the warehouse records its despatch as before) and, with a carrier, the parcels prefilled from the stock
   * item's weight and the courier's services (choice `service_code`, by 12 the default). Never books.
   */
  previewPickCourierBooking(
    pickId: string,
    input: BookPickCourierRequest,
  ): Promise<SupplierDryRun>;
  /**
   * Book the pick's despatch with the courier and record it under the courier's number. `request_id` is the
   * idempotency key: the same id again answers the same booking (`is_repeat`). Refusals are an `ApiError`: 400 with
   * field errors (`parcels.0.weight_kg`), 404 a pick out of scope, 409 an earlier attempt whose outcome is not known,
   * 503 the courier not reachable (nothing booked).
   */
  bookPickCourier(
    pickId: string,
    input: BookPickCourierRequest,
  ): Promise<BookPickCourierResponse>;
  /** The label PDF at the `label_url` a booking answered with (never built here). */
  downloadPickCourierLabel(labelUrl: string): Promise<Blob>;
}
